import { useQuery } from '@tanstack/react-query';

import api from 'api';
import type { Program, ProgramOption } from 'types';

export const programKeys = {
    byCandidate: (userId: number, country: string) => ['programs', userId, country] as const,
};

const toProgramOptions = (data: unknown): ProgramOption[] => {
    if (!Array.isArray(data) || data.length === 0) {
        return [];
    }

    const first: unknown = data[0];
    if (typeof first === 'object' && first !== null && 'program_name' in first) {
        return (data as Program[]).map((item) => ({
            value: item.program_name,
            label: item.program_name,
            display_color: item.display_color || 'black',
        }));
    }

    return (data as string[]).map((programName) => ({
        value: programName,
        label: programName,
        display_color: 'black',
    }));
};

export const usePrograms = (
    userId?: string | number | null,
    country?: string,
    options?: { enabled?: boolean }
) => {
    const numericUserId = Number(userId);
    const normalizedCountry = country?.trim() ?? '';
    const canQuery =
        Number.isFinite(numericUserId) && numericUserId > 0 && normalizedCountry.length > 0;

    return useQuery<ProgramOption[]>({
        queryKey: programKeys.byCandidate(
            Number.isFinite(numericUserId) ? numericUserId : 0,
            normalizedCountry
        ),
        queryFn: async () => {
            const response = await api.programList.get(numericUserId, normalizedCountry);
            if (response?.status !== 200 && response?.status !== 201) {
                return [];
            }
            return toProgramOptions(response?.data);
        },
        enabled: (options?.enabled ?? true) && canQuery,
        staleTime: 60 * 1000,
    });
};
